import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {GlobalSearchService, GlobalSearchResultModel, GlobalSearchResultGroupModel} from "./global-search.service";
import {BehaviorSubject, debounceTime} from "rxjs";
import {BaseComponent, GLOBAL, IdTextValueModel, LeModalService, simplifiedRegexSearch} from "common-shared";
import {filter, map, takeUntil} from "rxjs/operators";
import {EntityTypes, StateService} from "web-shared";
import {Router} from "@angular/router";
import {NgSelectComponent} from "@ng-select/ng-select";
import {GroupByPipe} from "ngx-pipes";
import {plainToInstance} from "class-transformer";
import {PropertyEditModalComponent} from "web-shared";
import {
  ManufacturingItemEditModalComponent
} from "../../manufacturing/manufacturing-item/manufacturing-item-edit-modal/manufacturing-item-edit-modal.component";
import {
  ManufacturingItemGroupEditModalComponent
} from "../../manufacturing/manufactured-item-groups/manufacturing-item-group-edit-modal/manufacturing-item-group-edit-modal.component";
import {
  ResourceEditModalComponent
} from "../../manufacturing/resources/resource-edit-modal/resource-edit-modal.component";

@Component({
  selector: 'mes-global-search',
  templateUrl: './global-search.component.html',
  styles: []
})
export class GlobalSearchComponent extends BaseComponent implements OnInit {
  searchString$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  results: GlobalSearchResultModel[] = [];
  groupedResults: GlobalSearchResultGroupModel[] = [];
  @ViewChild(NgSelectComponent) searchBox: NgSelectComponent;
  model: GlobalSearchResultModel;
  entityTypes = EntityTypes;
  Classificators = GLOBAL.Classificators;
  typeFilter: number[] = [];
  favourites: GlobalSearchResultModel[] = []

  constructor(private searchService: GlobalSearchService, private router: Router, private modalService: LeModalService, private groupByPipe: GroupByPipe, private stateService: StateService) {
    super();
  }

  ngOnInit(): void {
    this.searchString$.pipe(filter(value => value != null), debounceTime(300), takeUntil(this.componentDestroyed)).subscribe(searchString => {
      this.searchService.get(searchString).pipe(map(value => {
        this.results = value;
        this.groupedResults = [];
        const groupObject = this.groupByPipe.transform(value, 'Type');
        if (groupObject[EntityTypes.ItemGroup.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.ItemGroup, groupObject[EntityTypes.ItemGroup.toString()]));
        }
        if (groupObject[EntityTypes.Item.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Item, groupObject[EntityTypes.Item.toString()]));
        }
        if (groupObject[EntityTypes.Resource.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Resource, groupObject[EntityTypes.Resource.toString()]));
        }
        if (groupObject[EntityTypes.Property.toString()]) {
          this.groupedResults.push(this.getResultGroup(EntityTypes.Property, groupObject[EntityTypes.Property.toString()]));
        }
      }), takeUntil(this.componentDestroyed)).subscribe();
    });

    this.prepareFavourites();
  }

  @HostListener('document:keydown.alt.q')
  focusSearchBox() {
    setTimeout(() => {
      const element = document.getElementById('globalSearch');
      if (element) {
        element.scrollIntoView({behavior: 'smooth', block: "center"});
      }
    });
    this.searchBox.focus();
    this.searchBox.open();
  }

  search($event: { term: string, items: GlobalSearchResultModel[] }) {
    if ($event?.term?.length > 1) {
      this.searchString$.next($event.term);
    } else {
      this.setFavouritesAsResults();
    }
  }

  setFavouritesAsResults() {
    this.results = this.favourites;
    this.groupedResults = this.favourites.length ? [this.getResultGroup(999, this.results)] : [];
  }

  getResultGroup(groupCode: number, children: any[]): GlobalSearchResultGroupModel {
    let name: string;
    let cssClass: string;
    switch (groupCode) {
      case EntityTypes.Item: {
        name = 'Items';
        cssClass = 'bg-blue-300'
        break;
      }
      case EntityTypes.ItemGroup: {
        name = 'Item templates';
        cssClass = 'bg-blue-600 text-light'
        break;
      }
      case EntityTypes.Resource: {
        name = 'Resources';
        cssClass = 'bg-green-300'
        break;
      }
      case EntityTypes.Property: {
        name = 'Specs';
        cssClass = 'bg-indigo-400'
        break;
      }
      case 999: {
        name = 'Favourites';
        cssClass = 'bg-orange-500'
        break;
      }
      default:
        name = 'Other';
        cssClass = 'bg-default'
        break;
    }
    return {Name: name, Type: groupCode, Results: children, Length: children.length, CssClass: cssClass, IsCollapsed: false};
  };

  searchFn(term: string, item: GlobalSearchResultModel): boolean {
    const guidRegex = /^(\{?([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}}?)$/;
    const isGuid = guidRegex.test(term);
    return isGuid || simplifiedRegexSearch(term, [item.Code, item.Name]);
  }

  onChange(item: GlobalSearchResultModel) {
    if (item) {
      this.open(item, 'default');
      this.searchBox.close();
      setTimeout(() => {
        this.model = null;
      });
    }
  }

  onPaste(event: ClipboardEvent) {
    let pastedText = event.clipboardData.getData('text');
    pastedText = pastedText.trim();
    const inputElement = document.activeElement as HTMLInputElement;
    const finalCursorPosition = inputElement.selectionStart + pastedText.length;
    inputElement.setRangeText(pastedText);
    inputElement.setSelectionRange(finalCursorPosition, finalCursorPosition);
    this.searchBox.searchTerm = inputElement.value;
    this.search({term: inputElement.value, items: []});
    event.preventDefault();
  }

  open(item: GlobalSearchResultModel, target: 'modal' | 'blank' | 'default') {
    let namespace = '';
    let area = 'manufacturing';
    switch (item.Type) {
      case EntityTypes.Item: {
        namespace = 'item';
        break;
      }
      case EntityTypes.ItemGroup: {
        namespace = 'item-template';
        break;
      }
      case EntityTypes.Resource: {
        namespace = 'resource';
        break;
      }
      case EntityTypes.Property: {
        namespace = 'property';
        area = 'common';
        break
      }
    }

    if (namespace) {
      const commands = [area, namespace, 'edit', item.Id];

      if (target === 'default') {
        this.router.navigate(commands).catch(reason => console.error(reason));
      } else if (target === 'blank') {
        const url = this.router.serializeUrl(this.router.createUrlTree(commands));
        window.open(url, '_blank');
      } else if (target === 'modal') {
        this.openModal(item);
      }
    }
  }

  openModal(item: GlobalSearchResultModel) {
    switch (item.Type) {
      case EntityTypes.Item: {
        this.modalService.show(ManufacturingItemEditModalComponent, {initialState: {entityId: item.Id} as ManufacturingItemEditModalComponent, class: 'modal-xl'});
        break;
      }
      case EntityTypes.ItemGroup: {
        this.modalService.show(ManufacturingItemGroupEditModalComponent, {initialState: {entityId: item.Id} as ManufacturingItemGroupEditModalComponent, class: 'modal-xl'});
        break;
      }
      case EntityTypes.Resource: {
        this.modalService.show(ResourceEditModalComponent, {initialState: {entityId: item.Id} as ResourceEditModalComponent, class: 'modal-xl'});
        break;
      }
      case EntityTypes.Property: {
        this.modalService.show(PropertyEditModalComponent, {initialState: {propertyId: item.Id}});
        break;
      }
    }
  }

  openFavourite(favourite: GlobalSearchResultModel, target: 'blank' | 'default') {
    if (target === 'default') {
      if (favourite.Value.startsWith('http')) {
        window.open(favourite.Value, '_self');
      } else {
        this.router.navigate([favourite.Value]).catch(reason => console.error(reason));
      }
    } else if (target === 'blank') {
      window.open(favourite.Value, '_blank');
    }
  }

  toggleGroupFilter(group: number) {
    if (this.typeFilter.includes(group)) {
      this.typeFilter = this.typeFilter.filter(type => type !== group);
    } else {
      this.typeFilter.push(group);
    }

    this.groupedResults.map(resultGroup => {
      if (this.typeFilter.includes(resultGroup.Type)) {
        resultGroup.Results = [];
      } else if (!resultGroup.Results.length) {
        resultGroup.Results = this.results.filter(result => result.Type === resultGroup.Type);
      }
    });
    this.groupedResults = [...this.groupedResults];
    this.searchBox.close();
    this.searchBox.open();
  }

  private prepareFavourites() {
    this.stateService.currentUser$.pipe(takeUntil(this.componentDestroyed)).subscribe(user => {
      if (user?.Specs) {
        this.favourites = [];
        const favouritesSpec = user.Specs.find(spec => spec.PropertyId === GLOBAL.Properties.Favourites);
        if (favouritesSpec?.Value) {
          const favourites = plainToInstance(IdTextValueModel, <string[]>JSON.parse(favouritesSpec.Value));
          if (favourites.length) {
            favourites.forEach(favourite => {
              this.favourites.push(new GlobalSearchResultModel({Id: favourite.Id, Name: favourite.Text, Value: favourite.Value, Type: 999}));
            });
            if (!this.groupedResults.length) {
              this.setFavouritesAsResults();
            }
          }
        }
      }
    });
  }
}
